import React, { useEffect, useState } from 'react';
import {
  Alert, Box, Button, Card, CardContent, Chip, CircularProgress,
  Divider, Grid, InputAdornment, Snackbar, Stack, TextField, Typography,
} from '@mui/material';
import { Add, Delete, InfoOutlined, Save } from '@mui/icons-material';
import { adminApi } from '../../api/adminApi';

export default function TrialSettingsPage() {
  const [loading, setLoading]   = useState(true);
  const [saving, setSaving]     = useState(false);
  const [settings, setSettings] = useState({
    trialDays:    7,
    deletionDays: 14,
    reminderDays: [3, 1],
    updatedAt:    null,
    updatedBy:    null,
  });
  const [errors, setErrors]   = useState({});
  const [toast, setToast]     = useState({ open: false, msg: '', severity: 'success' });
  const [newReminder, setNewReminder] = useState('');

  useEffect(() => {
    (async () => {
      try {
        const data = await adminApi.getTrialSettings();
        setSettings(data);
      } catch (e) {
        showToast(`Failed to load settings: ${e.message}`, 'error');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  function showToast(msg, severity = 'success') {
    setToast({ open: true, msg, severity });
  }

  function validate() {
    const errs = {};
    const t = Number(settings.trialDays);
    const d = Number(settings.deletionDays);
    if (!t || t < 1 || t > 365) { errs.trialDays = 'Must be 1–365'; }
    if (!d || d < 1 || d > 365) { errs.deletionDays = 'Must be 1–365'; }
    if (d <= t) { errs.deletionDays = 'Must be greater than trial period'; }
    return errs;
  }

  async function handleSave() {
    const errs = validate();
    if (Object.keys(errs).length) { setErrors(errs); return; }
    setSaving(true);
    try {
      const updated = await adminApi.updateTrialSettings({
        trialDays:    Number(settings.trialDays),
        deletionDays: Number(settings.deletionDays),
        reminderDays: settings.reminderDays,
      });
      setSettings(updated);
      showToast('Trial settings saved.');
    } catch (e) {
      showToast(`Save failed: ${e.message}`, 'error');
    } finally {
      setSaving(false);
    }
  }

  function addReminder() {
    const v = Number(newReminder);
    if (!v || v < 1 || settings.reminderDays.includes(v)) { return; }
    setSettings(s => ({ ...s, reminderDays: [...s.reminderDays, v].sort((a, b) => b - a) }));
    setNewReminder('');
  }

  function removeReminder(day) {
    setSettings(s => ({ ...s, reminderDays: s.reminderDays.filter(d => d !== day) }));
  }

  if (loading) {
    return <Box display="flex" justifyContent="center" pt={8}><CircularProgress /></Box>;
  }

  return (
    <Box maxWidth={680} mx="auto" px={2} py={4}>
      <Typography variant="h5" fontWeight={700} mb={0.5}>Trial Settings</Typography>
      <Typography variant="body2" color="text.secondary" mb={3}>
        Configure the trial lifecycle for new self-service signups.
        Changes apply to newly created trials; existing trials retain their original dates.
      </Typography>

      <Stack spacing={3}>
        {/* ── Trial period ── */}
        <Card variant="outlined" sx={{ borderRadius: 2 }}>
          <CardContent sx={{ p: 3 }}>
            <Typography variant="subtitle1" fontWeight={700} mb={2}>
              Trial period
            </Typography>

            <Grid container spacing={3}>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Trial length (days)"
                  fullWidth
                  type="number"
                  value={settings.trialDays}
                  onChange={e => {
                    setSettings(s => ({ ...s, trialDays: e.target.value }));
                    setErrors(er => ({ ...er, trialDays: undefined }));
                  }}
                  error={!!errors.trialDays}
                  helperText={errors.trialDays || 'Account disabled after this many days'}
                  inputProps={{ min: 1, max: 365 }}
                  InputProps={{
                    endAdornment: <InputAdornment position="end">days</InputAdornment>,
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  label="Data retention (days)"
                  fullWidth
                  type="number"
                  value={settings.deletionDays}
                  onChange={e => {
                    setSettings(s => ({ ...s, deletionDays: e.target.value }));
                    setErrors(er => ({ ...er, deletionDays: undefined }));
                  }}
                  error={!!errors.deletionDays}
                  helperText={errors.deletionDays || 'Account deleted after this many days'}
                  inputProps={{ min: 1, max: 365 }}
                  InputProps={{
                    endAdornment: <InputAdornment position="end">days</InputAdornment>,
                  }}
                />
              </Grid>
            </Grid>

            <Alert severity="info" icon={<InfoOutlined />} sx={{ mt: 2, borderRadius: 1.5 }}>
              Timeline: signup → trial active ({settings.trialDays} days) → account disabled → data deleted at {settings.deletionDays} days
            </Alert>
          </CardContent>
        </Card>

        {/* ── Reminder emails ── */}
        <Card variant="outlined" sx={{ borderRadius: 2 }}>
          <CardContent sx={{ p: 3 }}>
            <Typography variant="subtitle1" fontWeight={700} mb={0.5}>
              Reminder emails
            </Typography>
            <Typography variant="body2" color="text.secondary" mb={2}>
              Automated emails sent to trial tenants before expiry.
            </Typography>

            <Stack direction="row" flexWrap="wrap" gap={1} mb={2}>
              {settings.reminderDays.map(day => (
                <Chip
                  key={day}
                  label={`${day} day${day !== 1 ? 's' : ''} before expiry`}
                  onDelete={() => removeReminder(day)}
                  deleteIcon={<Delete />}
                  color="primary"
                  variant="outlined"
                />
              ))}
              {settings.reminderDays.length === 0 && (
                <Typography variant="body2" color="text.secondary">No reminders configured.</Typography>
              )}
            </Stack>

            <Stack direction="row" spacing={1} alignItems="flex-start">
              <TextField
                label="Add reminder (days before)"
                size="small"
                type="number"
                value={newReminder}
                onChange={e => setNewReminder(e.target.value)}
                inputProps={{ min: 1 }}
                sx={{ width: 220 }}
                onKeyDown={e => e.key === 'Enter' && addReminder()}
              />
              <Button
                variant="outlined"
                startIcon={<Add />}
                onClick={addReminder}
                disabled={!newReminder}
                sx={{ whiteSpace: 'nowrap', borderRadius: 1.5 }}
              >
                Add
              </Button>
            </Stack>
          </CardContent>
        </Card>

        {/* ── Last saved ── */}
        {settings.updatedAt && (
          <Typography variant="caption" color="text.secondary">
            Last updated {new Date(settings.updatedAt).toLocaleString()}
            {settings.updatedBy && ` by ${settings.updatedBy}`}
          </Typography>
        )}

        <Divider />

        <Stack direction="row" justifyContent="flex-end" spacing={2}>
          <Button
            variant="contained"
            size="large"
            startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <Save />}
            onClick={handleSave}
            disabled={saving}
            sx={{ borderRadius: 2, px: 4 }}
          >
            Save settings
          </Button>
        </Stack>
      </Stack>

      <Snackbar
        open={toast.open}
        autoHideDuration={4000}
        onClose={() => setToast(t => ({ ...t, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={toast.severity} onClose={() => setToast(t => ({ ...t, open: false }))}>
          {toast.msg}
        </Alert>
      </Snackbar>
    </Box>
  );
}
